import type { ReactNode } from "react";
import { ThemeToggle } from "@humansignal/ui";
import { activeHostMode } from "../../utils/hostMode";
// Plain ``.css`` (NOT ``.prefix.css``) — the build's prefix-css
// transformer would otherwise add ``lsf-`` to every class name in
// the file (``.left`` → ``.lsf-left``), but the React components
// here render the un-prefixed names so they match the original
// Django ``login.css`` selectors visually 1:1.
import "./Auth.css";

/**
 * Two-column shell wrapping every React-rendered auth page (login,
 * signup). React port of the ``.login_page_new_ui`` markup in
 * ``label_studio/users/templates/users/new-ui/user_base.html`` —
 * gradient panel + brand-block on the left, the page's form on the
 * right. On mobile the inline media queries in Auth.prefix.css collapse
 * the layout to a single column with the brand-block above the form.
 *
 * Used only by the Capacitor bundle (and the new React /auth routes
 * for parity in browser dev). Web users at /user/login/ keep going
 * through the Django template — this component does not replace that.
 */
export const AuthShell = ({ children }: { children: ReactNode }) => {
  // Brand identity read from the injected APP_SETTINGS so a downstream
  // deployer can fork the reference build (AfriAnnotate) by only
  // rebuilding the SPA with different ``branding`` values — no source
  // edit required. Defaults preserved for the reference build. On
  // Capacitor the bundle's spa-template/index.html hardcodes these
  // values into APP_SETTINGS at build time; on web/desktop they come
  // from the server-side Branding provider.
  const appSettings = (window as any)?.APP_SETTINGS ?? {};
  const brandingCfg = appSettings.branding ?? {};
  // An extension host mode (the same bundle served as another product on
  // its own host) brings its own name, tagline and mark to the auth pages.
  const hostBrand = activeHostMode?.authBrand ?? null;
  const platformName: string = hostBrand?.name || brandingCfg.name || "AfriAnnotate";
  // A host brand may pass an empty tagline to show the mark and name only.
  const tagline: string =
    hostBrand && typeof hostBrand.tagline === "string"
      ? hostBrand.tagline
      : brandingCfg.tagline || appSettings.brand_tagline || "A collaborative data annotation platform";
  // Mark used in the brand block. Reference build used to ship the
  // animated variant (``anim.webp``) for the shorter framing-corner
  // treatment the design team prefers, but iOS WebKit's WebP
  // animation decoder segfaults on it (crash in
  // ``WebPAnimDecoderRestoreCanvas`` → ``EXC_BAD_ACCESS`` in the
  // WebContent process, taking the whole app down). Switched to the
  // static PNG mark — same silhouette, same brand recognition,
  // doesn't crash the WebView. Downstream deployers can still
  // override with ``branding.mark_url`` in their settings.
  const markUrl: string = hostBrand?.markUrl || brandingCfg.mark_url || "/static/brand/afriannotate/mark-256.png";
  const markUrlDark: string | undefined = hostBrand?.markUrlDark;
  const showReceiptLink = hostBrand?.receiptLink !== false && !window.location.pathname.startsWith("/verify-receipt");

  return (
    <div className="login_page_new_ui">
      <div className="left">
        <div className="brand-block">
          {markUrlDark ? (
            <>
              <img src={markUrl} alt={`${platformName} logo`} className="ls-logo afri-auth-logo--light" />
              <img src={markUrlDark} alt="" aria-hidden="true" className="ls-logo afri-auth-logo--dark" />
            </>
          ) : (
            <img src={markUrl} alt={`${platformName} logo`} className="ls-logo" />
          )}
          <h2>{platformName}</h2>
          {tagline ? <h3>{tagline}</h3> : null}
        </div>
      </div>
      <div className="right">
        {children}
        {/* Public receipt check — visible on every auth page so a
            person handed a payout receipt can verify it without an
            account. Hidden on the verify page itself. */}
        {showReceiptLink && (
          <p style={{ marginTop: 14, fontSize: 13, textAlign: "center" }}>
            <a href="/verify-receipt" style={{ color: "var(--color-primary-content, #4c5fa9)" }}>
              Verify a payout receipt →
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * The theme switch on an auth card, at the right of its title row: light,
 * dark or the device's own, changed without signing in and remembered for
 * the app (the same choice as in the profile menu).
 */
export const CardThemeToggle = () => (
  <div className="auth-card-theme">
    <ThemeToggle />
  </div>
);
